<template>
  <!-- The whole assistant is administrator-only (#661). The gate sits here rather than on each
       surface: `showStepsDialog` is module-level state that any caller can flip — the profile
       menu did, for every user — and only the FAB used to check the role. A non-administrator
       never even loads the steps (`loadStatus` returns early), so what opened was an empty
       « Configuration initiale » at 0 / 0. -->
  <template v-if="isAdmin">
  <!-- Welcome dialog — shown only when no steps are completed -->
  <BaseDialog v-model="showWelcomeDialog" :title="translate('parcours.management.initWizard.welcome')" icon="fa-rocket" :max-width="DIALOG_WIDTH.standard" persistent>
    <p class="text-body-1 mb-4">
      {{ translate('parcours.management.initWizard.welcomeMessage') }}
    </p>
    <p class="text-body-2 text-medium-emphasis">
      {{ translate('parcours.management.initWizard.stepCount', { total: totalCount, required: requiredCount }) }}
    </p>
    <template #actions>
      <v-btn variant="text" @click="showWelcomeDialog = false">{{ translate('parcours.management.initWizard.later') }}</v-btn>
      <v-btn color="primary" variant="flat" @click="startConfiguration">
        <v-icon start>fas fa-play</v-icon>
        {{ translate('parcours.management.initWizard.startConfiguration') }}
      </v-btn>
    </template>
  </BaseDialog>

  <!-- Steps dialog — shows all steps with status -->
  <BaseDialog v-model="showStepsDialog" :title="translate('parcours.management.initWizard.initialConfig')" icon="fa-clipboard-list" :max-width="DIALOG_WIDTH.large">
    <!-- Progress bar -->
    <div class="mb-4">
      <div class="d-flex justify-space-between align-center mb-1">
        <span class="text-body-2 text-medium-emphasis">{{ translate('parcours.management.initWizard.progress') }}</span>
        <span class="text-body-2 font-weight-medium">{{ completedCount }} / {{ totalCount }}</span>
      </div>
      <v-progress-linear :model-value="(completedCount / totalCount) * 100" color="primary" height="8" rounded />
    </div>

    <!-- Steps list -->
    <v-list density="compact" class="pa-0">
      <v-list-item v-for="step in steps" :key="step.key" class="cursor-pointer"
        :density="step.completed ? 'compact' : 'default'" rounded @click="navigateToStep(step)">
        <template #prepend>
          <v-icon :color="step.completed ? 'grey' : (step.optional ? 'grey' : 'warning')" :icon="step.completed
            ? 'fas fa-check-circle'
            : (step.optional ? 'far fa-circle' : 'far fa-circle')" size="small" />
        </template>

        <v-list-item-title :class="step.completed ? 'text-medium-emphasis text-body-2 step-link' : ''">
          <v-icon v-if="!step.completed" size="x-small" class="mr-1">{{ step.icon }}</v-icon>
          {{ step.title }}
          <v-chip v-if="step.optional && !step.completed" size="x-small" variant="outlined"
            class="ml-1">{{ translate('parcours.management.initWizard.optional') }}</v-chip>
        </v-list-item-title>
        <v-list-item-subtitle v-if="!step.completed">{{ step.description }}</v-list-item-subtitle>

        <template #append>
          <v-icon v-if="!step.completed" size="small" color="grey">fas fa-chevron-right</v-icon>
        </template>
      </v-list-item>
    </v-list>

    <template #actions-leading>
      <v-btn variant="text" size="small" @click="dismissWizard">
        <v-icon start size="small">fas fa-eye-slash</v-icon>
        {{ translate('parcours.management.initWizard.hideWizard') }}
      </v-btn>
    </template>
  </BaseDialog>

  <!-- FAB — visible when steps remain and user is admin -->
  <v-btn v-if="showFab" class="initialization-fab" color="primary" icon :aria-label="translate('parcours.management.initWizard.openConfig')" size="large" elevation="8"
    @click="openStepsDialog">
    <v-badge :content="`${completedCount}/${totalCount}`" color="warning" offset-x="-4" offset-y="-4">
      <v-icon>fas fa-clipboard-list</v-icon>
    </v-badge>
  </v-btn>
  </template>
</template>

<script setup lang="ts">
import { translate } from '@fondationcss/sdk';
import { BaseDialog } from '@fondationcss/ui';
import { useInitializationWizard } from '@/composables/use-initialization-wizard';
import { DIALOG_WIDTH } from '@/utils/dialog-width';

const {
  isAdmin,
  steps,
  completedCount,
  requiredCount,
  totalCount,
  showFab,
  showWelcomeDialog,
  showStepsDialog,
  openStepsDialog,
  startConfiguration,
  navigateToStep,
  dismissWizard,
} = useInitializationWizard();
</script>

<style scoped>
.initialization-fab {
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 1000;
}

.cursor-pointer {
  cursor: pointer;
}

.step-link {
  text-decoration: none;
}

.step-link:hover {
  text-decoration: underline;
}
</style>
